import { useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Mic2,
  Disc3,
  TrendingUp,
  Users,
} from 'lucide-react'
import BrandLogo from '../../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Wrapped Slide Components
   Year-in-review cards. Dark theme matching the DIZEE deck language:
   #13120A bg, white text, #FF2300 accent, glassmorphism surfaces.
   Each slide is self-contained and independently reusable.
   ═══════════════════════════════════════════════════════════════════════════ */

const BRAND = '#FF2300'
const BG = '#13120A'

/* ── Shared primitives ──────────────────────────────────────────────────── */

export function SlideShell({
  children,
  className = '',
  style = {},
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div
      className={`relative w-full max-w-[400px] mx-auto rounded-2xl overflow-hidden border border-white/[0.08] ${className}`}
      style={{
        minHeight: 580,
        backgroundColor: BG,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function SlideHeader() {
  return (
    <div className="flex items-center justify-between px-6 pt-5 pb-3">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-2.5 w-auto text-white" />
      </div>
      <span className="text-[9px] font-medium text-zinc-700 uppercase tracking-widest">
        Year in Review
      </span>
    </div>
  )
}

function SlideTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest">
      {children}
    </span>
  )
}

function GlassCard({
  children,
  className = '',
  glow = false,
}: {
  children: React.ReactNode
  className?: string
  glow?: boolean
}) {
  return (
    <div
      className={`rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm ${className}`}
      style={glow ? { boxShadow: '0 0 40px -12px rgba(255,35,0,0.15)' } : {}}
    >
      {children}
    </div>
  )
}

function StatRow({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-2.5 border-b border-white/[0.04] last:border-0">
      <span className="text-[13px] text-zinc-500">{label}</span>
      <span className={`text-[15px] font-semibold ${accent ? 'text-[#FF2300]' : 'text-white'}`}>
        {value}
      </span>
    </div>
  )
}

function TabBar({
  tabs,
  active,
  onSelect,
}: {
  tabs: string[]
  active: string
  onSelect: (tab: string) => void
}) {
  return (
    <div className="flex gap-1.5 mb-5">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => onSelect(tab.toLowerCase())}
          className={`text-[11px] font-medium px-3 py-1.5 rounded-lg transition-colors ${
            active === tab.toLowerCase()
              ? 'bg-white/[0.08] text-white'
              : 'text-zinc-600 hover:text-zinc-400'
          }`}
        >
          {tab}
        </button>
      ))}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 1 — Intro / Hero
   Dark with subtle DIZEE red accent line
   ═══════════════════════════════════════════════════════════════════════════ */

export function SlideIntro({ onStart }: { onStart?: () => void }) {
  return (
    <SlideShell>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#FF2300]/60 to-transparent" />
      <SlideHeader />
      <div className="flex flex-col justify-between px-6 pb-8" style={{ minHeight: 500 }}>
        <div className="mt-10">
          <SlideTag>2025 Recap</SlideTag>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-white leading-[1.08]">
            Your Year<br />in Motion
          </h1>
          <p className="mt-4 text-base text-zinc-400 leading-relaxed max-w-[260px]">
            Your music. Your shows. Your momentum.
          </p>
          <div className="mt-8 h-[1px] w-16 bg-white/[0.08]" />
        </div>
        <button
          onClick={onStart}
          className="mt-auto self-start rounded-lg bg-white px-8 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
        >
          Start
        </button>
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 2 — Category Chooser
   ═══════════════════════════════════════════════════════════════════════════ */

const CATEGORIES: { label: string; sublabel: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { label: 'Shows', sublabel: 'Live performance data', Icon: Mic2 },
  { label: 'Releases', sublabel: 'Music & streaming metrics', Icon: Disc3 },
  { label: 'Growth', sublabel: 'Audience & reach', Icon: TrendingUp },
  { label: 'Fans', sublabel: 'Community insights', Icon: Users },
]

export function SlideCategoryChooser({ onSelect }: { onSelect?: (category: string) => void }) {
  return (
    <SlideShell>
      <SlideHeader />
      <div className="px-6 pt-4 pb-8">
        <SlideTag>Explore</SlideTag>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white leading-tight mb-6">
          Choose what<br />defined your year
        </h2>
        <div className="space-y-2.5">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.label}
              onClick={() => onSelect?.(cat.label.toLowerCase())}
              className="group w-full flex items-center gap-4 rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-3.5 text-left hover:bg-white/[0.06] hover:border-white/[0.12] transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                <cat.Icon className="w-4 h-4 text-zinc-400 group-hover:text-[#FF2300] transition-colors" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-[14px] font-medium text-white">{cat.label}</span>
                <p className="text-[11px] text-zinc-600">{cat.sublabel}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 3 — Your Shows (Summary)
   ═══════════════════════════════════════════════════════════════════════════ */

interface ShowsSummaryData {
  totalShows: number
  cities: number
  bestCity: string
  highestTurnout: number
}

const DEFAULT_SHOWS_SUMMARY: ShowsSummaryData = {
  totalShows: 42,
  cities: 18,
  bestCity: 'Toronto',
  highestTurnout: 1240,
}

function MiniCityChart({ highlight = 3 }: { highlight?: number }) {
  const bars = [45, 70, 55, 90, 60, 75, 50, 85, 65, 80]
  return (
    <div className="flex items-end gap-[3px] h-8">
      {bars.map((h, i) => (
        <div
          key={i}
          className="w-[4px] rounded-sm"
          style={{
            height: `${h}%`,
            backgroundColor: i === highlight ? BRAND : 'rgba(255,255,255,0.08)',
          }}
        />
      ))}
    </div>
  )
}

export function SlideShowsSummary({ data = DEFAULT_SHOWS_SUMMARY }: { data?: ShowsSummaryData }) {
  return (
    <SlideShell>
      <SlideHeader />
      <div className="px-6 pt-2 pb-8">
        <SlideTag>Shows</SlideTag>
        <h2 className="mt-3 text-xl font-semibold tracking-tight text-white mb-5">Your shows</h2>

        <GlassCard className="p-5 mb-3" glow>
          <p className="text-3xl font-semibold tracking-tighter text-white">{data.totalShows} Shows</p>
          <p className="text-[13px] text-zinc-500 mt-1">Across {data.cities} cities</p>
        </GlassCard>

        <GlassCard className="p-4 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-medium text-zinc-300">{data.bestCity}</span>
            <MiniCityChart />
          </div>
        </GlassCard>

        <GlassCard className="p-4 mb-3">
          <StatRow label="Best city" value={data.bestCity} />
          <StatRow label="Highest turnout" value={`${data.highestTurnout.toLocaleString()} tickets`} accent />
        </GlassCard>
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 4 — Your Shows (Detail)
   ═══════════════════════════════════════════════════════════════════════════ */

interface ShowsDetailData {
  totalShows: number
  cities: number
  topCity: string
  topCityTurnout: number
  sellOutRate: number
}

const DEFAULT_SHOWS_DETAIL: ShowsDetailData = {
  totalShows: 42,
  cities: 18,
  topCity: 'Toronto',
  topCityTurnout: 1240,
  sellOutRate: 68,
}

function HorizontalBarChart() {
  const cities = [
    { name: 'Toronto', value: 92 },
    { name: 'New York', value: 78 },
    { name: 'LA', value: 65 },
    { name: 'Miami', value: 58 },
    { name: 'Chicago', value: 45 },
  ]
  return (
    <div className="space-y-2">
      {cities.map((city) => (
        <div key={city.name} className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-600 w-14 text-right font-medium">{city.name}</span>
          <div className="flex-1 h-3 rounded-sm bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-sm transition-all"
              style={{
                width: `${city.value}%`,
                backgroundColor: city.name === 'Toronto' ? BRAND : 'rgba(255,255,255,0.1)',
              }}
            />
          </div>
          <span className="text-[10px] text-zinc-600 w-6">{city.value}%</span>
        </div>
      ))}
    </div>
  )
}

export function SlideShowsDetail({ data = DEFAULT_SHOWS_DETAIL }: { data?: ShowsDetailData }) {
  return (
    <SlideShell>
      <SlideHeader />
      <div className="px-6 pt-2 pb-8">
        <SlideTag>Shows</SlideTag>
        <h2 className="mt-3 text-xl font-semibold tracking-tight text-white mb-5">Your shows</h2>

        <GlassCard className="p-5 mb-3" glow>
          <p className="text-3xl font-semibold tracking-tighter text-white">{data.totalShows} Shows</p>
          <p className="text-[13px] text-zinc-500 mt-1">Across {data.cities} cities</p>
        </GlassCard>

        <GlassCard className="p-4 mb-3">
          <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-3">Top cities</p>
          <HorizontalBarChart />
        </GlassCard>

        <div className="grid grid-cols-2 gap-2.5">
          <GlassCard className="p-4">
            <p className="text-[11px] text-zinc-600 mb-1">Highest turnout</p>
            <p className="text-lg font-semibold text-white tracking-tight">
              {data.topCityTurnout.toLocaleString()}
            </p>
            <p className="text-[10px] text-zinc-600 mt-0.5">tickets</p>
          </GlassCard>

          <GlassCard className="p-4">
            <p className="text-[11px] text-zinc-600 mb-1">Sell-out rate</p>
            <p className="text-lg font-semibold text-white tracking-tight">{data.sellOutRate}%</p>
            <div className="mt-2 h-1 w-full rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full bg-[#FF2300]"
                style={{ width: `${data.sellOutRate}%` }}
              />
            </div>
          </GlassCard>
        </div>
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 5 — Critical Moments
   ═══════════════════════════════════════════════════════════════════════════ */

interface CriticalMomentsData {
  atRiskShows: number
  insight: string
}

const DEFAULT_CRITICAL: CriticalMomentsData = {
  atRiskShows: 3,
  insight: 'Driven by pacing + promoter updates',
}

export function SlideCriticalMoments({ data = DEFAULT_CRITICAL }: { data?: CriticalMomentsData }) {
  const [activeTab, setActiveTab] = useState('shows')

  return (
    <SlideShell>
      <SlideHeader />
      <div className="px-6 pt-2 pb-8">
        <SlideTag>Insights</SlideTag>
        <h2 className="mt-3 text-2xl font-semibold tracking-tight text-white mb-4">
          Critical moments
        </h2>

        <TabBar tabs={['Shows', 'Music', 'Fans']} active={activeTab} onSelect={setActiveTab} />

        <GlassCard className="p-5 mb-4" glow>
          <p className="text-[11px] font-medium text-[#FF2300] uppercase tracking-wider mb-2">At risk</p>
          <p className="text-3xl font-semibold tracking-tighter text-white leading-tight">
            {data.atRiskShows} shows<br />were at risk
          </p>
        </GlassCard>

        {/* Abstract data viz — stacked translucent bars */}
        <GlassCard className="p-4 mb-4 overflow-hidden">
          <div className="flex gap-1 h-20 items-end">
            {Array.from({ length: 16 }).map((_, i) => {
              const h = 20 + Math.sin(i * 0.8) * 30 + Math.cos(i * 1.2) * 25
              const isRisk = i === 4 || i === 9 || i === 13
              return (
                <div key={i} className="flex-1 rounded-sm" style={{
                  height: `${Math.max(h, 15)}%`,
                  backgroundColor: isRisk ? 'rgba(255,35,0,0.5)' : 'rgba(255,255,255,0.06)',
                }} />
              )
            })}
          </div>
        </GlassCard>

        <p className="text-[12px] text-zinc-500">{data.insight}</p>

        <div className="flex items-center justify-center gap-4 mt-6">
          <button className="w-7 h-7 rounded-lg border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.04] transition">
            <ChevronLeft className="w-3.5 h-3.5 text-zinc-500" />
          </button>
          <span className="text-[11px] text-zinc-500 font-medium">Shows</span>
          <button className="w-7 h-7 rounded-lg border border-white/[0.08] flex items-center justify-center hover:bg-white/[0.04] transition">
            <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        </div>
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE 6 — Your Music
   ═══════════════════════════════════════════════════════════════════════════ */

interface MusicData {
  releases: number
  streams: string
  playlistsAdded: number
  topRelease: string
}

const DEFAULT_MUSIC: MusicData = {
  releases: 6,
  streams: '120K',
  playlistsAdded: 18,
  topRelease: 'Midnight Drive',
}

function MiniStreamChart() {
  const bars = [20, 35, 50, 40, 65, 80, 70, 90, 75, 85, 60, 95]
  return (
    <div className="flex items-end gap-[2px] h-8">
      {bars.map((h, i) => (
        <div
          key={i}
          className="w-[3px] rounded-sm"
          style={{
            height: `${h}%`,
            backgroundColor: `rgba(255, 35, 0, ${0.25 + (i / bars.length) * 0.55})`,
          }}
        />
      ))}
    </div>
  )
}

function TopReleaseCard({ title }: { title: string }) {
  return (
    <GlassCard className="p-5 overflow-hidden relative" glow>
      <p className="text-[11px] text-zinc-600 mb-1">Top release</p>
      <p className="text-lg font-semibold text-white tracking-tight leading-tight">
        {title}
      </p>
      {/* Waveform */}
      <div className="mt-4 flex items-end gap-[2px] h-8">
        {Array.from({ length: 32 }).map((_, i) => {
          const h = 25 + Math.sin(i * 0.6) * 25 + Math.cos(i * 1.1) * 20
          return (
            <div
              key={i}
              className="flex-1 rounded-sm bg-white/[0.08]"
              style={{ height: `${Math.max(h, 12)}%` }}
            />
          )
        })}
      </div>
      {/* Vinyl circle */}
      <div className="absolute top-4 right-4 w-16 h-16 rounded-full border-2 border-white/[0.06] bg-white/[0.03] flex items-center justify-center">
        <div className="w-3 h-3 rounded-full border border-white/[0.08] bg-white/[0.04]" />
      </div>
    </GlassCard>
  )
}

export function SlideYourMusic({ data = DEFAULT_MUSIC }: { data?: MusicData }) {
  const [activeTab, setActiveTab] = useState('music')

  return (
    <SlideShell>
      <SlideHeader />
      <div className="px-6 pt-2 pb-8">
        <SlideTag>Music</SlideTag>
        <h2 className="mt-3 text-xl font-semibold tracking-tight text-white mb-4">Your music</h2>

        <TabBar tabs={['Shows', 'Music', 'Fans']} active={activeTab} onSelect={setActiveTab} />

        <GlassCard className="p-4 mb-2.5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-semibold tracking-tighter text-white">{data.releases} releases</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4 mb-2.5">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold tracking-tighter text-white">{data.streams} streams</p>
            <MiniStreamChart />
          </div>
        </GlassCard>

        <GlassCard className="p-4 mb-4">
          <p className="text-2xl font-semibold tracking-tighter text-white">{data.playlistsAdded} playlists added</p>
        </GlassCard>

        <TopReleaseCard title={data.topRelease} />
      </div>
    </SlideShell>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   ALL SLIDES — Export array for easy iteration
   ═══════════════════════════════════════════════════════════════════════════ */

export const WRAPPED_SLIDES = [
  { id: 'intro', title: 'Intro', component: SlideIntro },
  { id: 'category', title: 'Category Chooser', component: SlideCategoryChooser },
  { id: 'shows-summary', title: 'Your Shows', component: SlideShowsSummary },
  { id: 'shows-detail', title: 'Shows Detail', component: SlideShowsDetail },
  { id: 'critical', title: 'Critical Moments', component: SlideCriticalMoments },
  { id: 'music', title: 'Your Music', component: SlideYourMusic },
] as const
